import { call, callForm } from "../../util/action";

import { AUDIENCE_TARGET_TYPES, TARGETING_GROUPS } from "./fields/questionOptions";

export function buildListUrl(descriptor, { page = 1, size = 25, search = "", filters = {}, ordering = "" } = {}) {
  const parts = [`page=${page}`];
  // The table owns the page size (its footer offers 25/50/100); 25 is the API's
  // StandardResultsSetPagination default and the fallback for any caller that omits it.
  parts.push(`page_size=${size}`);
  if (search) parts.push(`search=${encodeURIComponent(search)}`);
  if (ordering) parts.push(`ordering=${encodeURIComponent(ordering)}`);
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") parts.push(`${k}=${encodeURIComponent(v)}`);
  });
  return `${descriptor.endpoint}?${parts.join("&")}`;
}

export function fetchList(descriptor, params, cb) {
  call({ method: "GET", url: buildListUrl(descriptor, params) }, r => {
    if (Array.isArray(r)) return cb({ results: r, count: r.length });
    if (r && Array.isArray(r.results)) return cb({ results: r.results, count: r.count ?? r.results.length });
    cb({ results: [], count: 0 });
  });
}

export function fetchItem(descriptor, id, cb) {
  call({ method: "GET", url: `${descriptor.endpoint}${id}/` }, cb);
}

/** An entity picker hands back the full option OBJECT so a chip can name itself; the API wants pks.
 *  One definition, used for top-level entity fields and for the two field types that nest their own
 *  pickers (audience targets, per-question targeting). */
const toId = x => (x && typeof x === "object" ? x.id ?? x.value ?? x : x);
const toIds = list => (Array.isArray(list) ? list.map(toId) : list);

export function saveItem(descriptor, item, me, cb) {
  const isNew = !item.id;
  let payload = { ...item };
  if (isNew && typeof descriptor.injectOnCreate === "function") payload = descriptor.injectOnCreate(payload, me);
  (descriptor.fields || []).forEach(f => {
    if (f.type !== "entity") return;
    const v = payload[f.name];
    if (v == null) return;
    payload[f.name] = Array.isArray(v) ? toIds(v) : toId(v);
  });

  /* The two field types whose pickers are NESTED, and so are not reached by the loop above. Both
     used to flatten to ids inside the renderer, which is why their chips could not label themselves
     once the loaded option page moved on (PICSELL-1574 #22, #34) — the unwrapping belongs at the
     edge, where the payload is built, not in the editor. */
  (descriptor.fields || []).forEach(f => {
    if (f.type === "audience") {
      AUDIENCE_TARGET_TYPES.forEach(target => {
        if (target.key in payload) payload[target.key] = toIds(payload[target.key]);
      });
    }
    if (f.type === "questions" && Array.isArray(payload[f.name])) {
      payload[f.name] = payload[f.name].map(question => {
        const out = { ...question };
        TARGETING_GROUPS.forEach(g => {
          if (g.key in out) out[g.key] = toIds(out[g.key]);
        });
        return out;
      });
    }
  });

  // An `audience` field owns no key of its own: a survey targets exactly one of points /
  // point_groups / networks, so `AudienceField` writes into those three directly. `emptyValueFor`
  // still seeds a placeholder under the field's own name, which would otherwise be POSTed as a
  // phantom `audience: null` on every create.
  (descriptor.fields || []).forEach(f => {
    if (f.type === "audience") delete payload[f.name];
  });

  // Image fields: only resend a File (new upload) or "" (explicit clear).
  // An unchanged value is a URL string (or null) fetched from GET — the API can't accept that back.
  const clearedImages = [];
  (descriptor.fields || []).forEach(f => {
    if (f.type !== "image") return;
    const v = payload[f.name];
    if (typeof File !== "undefined" && v instanceof File) return;
    if (v === "") {
      clearedImages.push(f.name);
      return;
    }
    delete payload[f.name];
  });

  const hasFile = Object.values(payload).some(v => typeof File !== "undefined" && v instanceof File);

  /* ⚠ How a cleared image is spelled depends on the ENCODING, because DRF only coerces `""` on its
     HTML/multipart branch:
       multipart — `""` becomes the `empty` sentinel, which a partial update turns into `SkipField`;
                   with `allow_null` on the serializer it becomes None, which its `validate()`
                   normalises back to `""` (the column is NOT NULL, so `""` is the stored "no image").
       JSON      — `""` goes straight to `ImageField.to_internal_value`, where `data.name` raises and
                   DRF answers 400 "The submitted data was not a file. Check the encoding type on the
                   form." That was the network-logo bug verbatim (PICSELL-1574 #32), and since a
                   clear carries no File, JSON is exactly the branch it takes.
     So: `null` over JSON, `""` over multipart. Needs `image = ImageField(allow_null=True)` on the
     serializer — see NetworkWriteSerializer in picsell-api. */
  if (hasFile) {
    const fd = new FormData();
    /* ⚠ The object branch is unreachable today and wrong if it is ever reached: a nested object (a
       point's `location`) would arrive server-side as a JSON STRING, which the field cannot parse.
       It needs no fix because no descriptor has both an image field and an object field — `points`
       has `location` and no image, `networks` has an image and no object. Give one both and this is
       the line to revisit. */
    Object.entries(payload).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (typeof File !== "undefined" && v instanceof File) fd.append(k, v);
      else if (Array.isArray(v)) v.forEach(item => fd.append(k, item));
      else if (typeof v === "object") fd.append(k, JSON.stringify(v));
      else fd.append(k, v);
    });
    callForm({ method: isNew ? "POST" : "PATCH", url: isNew ? descriptor.endpoint : `${descriptor.endpoint}${item.id}/`, post: fd }, cb);
    return;
  }

  clearedImages.forEach(name => {
    payload[name] = null;
  });

  call(
    {
      method: isNew ? "POST" : "PATCH",
      url: isNew ? descriptor.endpoint : `${descriptor.endpoint}${item.id}/`,
      post: payload,
    },
    cb
  );
}

export function deleteItem(descriptor, id, cb) {
  call({ method: "DELETE", url: `${descriptor.endpoint}${id}/` }, r => cb(!!r && r.status === 204));
}

export function patchItem(descriptor, id, data, cb) {
  call({ method: "PATCH", url: `${descriptor.endpoint}${id}/`, post: data }, cb);
}

// Normalizes a descriptor's deactivation config, whether declared via the new
// `deactivation` shape or the legacy `softDelete: true` shorthand.
export function getDeactivation(descriptor) {
  if (descriptor.deactivation) {
    const d = descriptor.deactivation;
    return {
      field: d.field,
      activeValue: d.activeValue,
      inactiveValue: "inactiveValue" in d ? d.inactiveValue : !d.activeValue,
      via: d.via || "patch",
    };
  }
  if (descriptor.softDelete) {
    return { field: "is_removed", activeValue: false, inactiveValue: true, via: "delete" };
  }
  return null;
}

export function isInactive(item, cfg) {
  return cfg ? item[cfg.field] !== cfg.activeValue : false;
}

/* `parseErrors` lives in `util/action.js` now — a 400 body is that file's business, and the
   merchandiser page needed the same reader. */
